import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { EmbeddedBlock, EmbeddedBlockVariant } from '@tinct/core-api-models';
import type { LandingPageUrls } from '../../../landing-page-editor/landing-page-editor';
import { usePreloadedPageHtml } from '../../hooks/use-preloaded-page-html';
import { isBlockPersonalizationEnabled } from '../../../../utils/is-block-personalization-enabled';
import { StudioCanvas } from './studio-canvas';
import { CanvasArtboard, type CanvasArtboardDefinition } from './canvas-artboard';
import { STUDIO_DEVICES, selectDevicePage } from '../../utils/studio-devices';

type LandingPageCanvasPanelProps = {
  landingPageUrls?: LandingPageUrls | null;
  loading?: boolean;
  blocks?: EmbeddedBlock[];
  includeDisabledBlocks?: boolean;
  blockVariants?: EmbeddedBlockVariant[];
  selectedBlockId?: string | null;
  onBlockSelect?: (blockId: string | null) => void;
  overlay?: ReactNode;
  errorState?: ReactNode;
  /**
   * Rendered inside the card, flush with its left edge, beside the canvas
   * (the studio's pages panel). Must size itself in-flow.
   */
  sidePanel?: ReactNode;
  /** Forwarded to the active artboard: `text` enables inline text edition. */
  interactionMode?: 'select' | 'text';
  onVariantEditCommit?: (blockVariantId: string, html: string) => Promise<string | null | undefined>;
  /** Forwarded to the artboards: makes the selected block's badge editable. */
  onBlockUpdate?: (block: EmbeddedBlock) => void;
};

/**
 * One artboard per studio device, each at the dimension key its device
 * resolves to among the keys the version actually carries (preview-iframe
 * contract rule 2: the key comes from the device, never from a container
 * width) — preferred Large desktop 1400x900, Tablet 800x800, Phone
 * 400x800, else the packaged key nearest in width. Devices resolving to the
 * same key (a version packaged at fewer widths) collapse into one artboard.
 * NEW-origin responsive docs share one URL under every key, EXISTING-origin
 * captures each get theirs.
 */
function deriveArtboards(urls: LandingPageUrls | null | undefined): CanvasArtboardDefinition[] {
  const artboards: CanvasArtboardDefinition[] = [];
  for (const device of STUDIO_DEVICES) {
    const page = selectDevicePage(urls, device.id);
    if (!page || artboards.some((artboard) => artboard.key === page.key)) continue;
    artboards.push({ ...page, label: device.label });
  }
  return artboards;
}

/**
 * The Framer-style canvas mode of the studio: every responsive version of
 * the page side by side on a pannable/zoomable surface. Same props as
 * LandingPagePanel so the two render modes are interchangeable.
 */
export const LandingPageCanvasPanel = ({
  landingPageUrls,
  loading = false,
  blocks = [],
  includeDisabledBlocks = false,
  blockVariants = [],
  selectedBlockId = null,
  onBlockSelect,
  overlay,
  errorState,
  sidePanel,
  interactionMode,
  onVariantEditCommit,
  onBlockUpdate,
}: LandingPageCanvasPanelProps) => {
  // The personalization toggle is applied here, at preview time: variants are
  // generated for every block, so a toggle just re-filters what gets applied —
  // no regeneration involved.
  // Memoized so the artboards (memo'd) see stable identities across renders.
  const enabledBlocks = useMemo(
    () => (includeDisabledBlocks ? blocks : blocks.filter(isBlockPersonalizationEnabled)),
    [blocks, includeDisabledBlocks],
  );
  const enabledBlockVariants = useMemo(() => {
    const enabledBlockIds = new Set(enabledBlocks.map((block) => block.id));
    return blockVariants.filter(
      (variant) => variant.block?.id && enabledBlockIds.has(variant.block.id),
    );
  }, [blockVariants, enabledBlocks]);

  const artboards = useMemo(() => deriveArtboards(landingPageUrls), [landingPageUrls]);

  const { htmlByUrl, isLoading: isPreloading } = usePreloadedPageHtml(landingPageUrls);
  const isLoading = loading || isPreloading;

  // Overlays run on one artboard at a time (pointer-enter activates); the
  // widest one starts active.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const effectiveActiveKey = activeKey ?? artboards[0]?.key ?? null;
  const handleActivate = useCallback((key: string) => setActiveKey(key), []);

  if (errorState) {
    return (
      <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {sidePanel}
        {errorState}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-3xl border border-border bg-background shadow-lg mb-2 mr-2">
      {sidePanel}
      {/* StudioCanvas's viewport is `absolute inset-0`: this wrapper is its
          containing block, so the canvas (and the overlay) fill the space
          beside the side panel rather than the whole card. */}
      <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
        {artboards.length === 0 || isLoading ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            {isLoading ? 'Loading page...' : 'No landing page URL provided'}
          </div>
        ) : (
          <StudioCanvas>
            {({ onDocumentWheel }) => (
              <>
                {artboards.map((artboard) => (
                  <CanvasArtboard
                    key={artboard.key}
                    definition={artboard}
                    html={htmlByUrl[artboard.url] ?? ''}
                    isHtmlLoading={!htmlByUrl[artboard.url]}
                    blocks={enabledBlocks}
                    blockVariants={enabledBlockVariants}
                    selectedBlockId={selectedBlockId}
                    onBlockSelect={onBlockSelect}
                    onBlockUpdate={onBlockUpdate}
                    interactionMode={interactionMode}
                    onVariantEditCommit={onVariantEditCommit}
                    isActive={artboard.key === effectiveActiveKey}
                    onActivate={handleActivate}
                    onDocumentWheel={onDocumentWheel}
                  />
                ))}
              </>
            )}
          </StudioCanvas>
        )}
        {overlay}
      </div>
    </div>
  );
};
